import {
  Table,
  Button,
  Row,
  Col,
  Form,
} from "react-bootstrap";
import { useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useGetProductsQuery } from "../../slices/productsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetTransfersQuery } from "../../slices/transfersApiSlice";
import { useGetStocksQuery } from "../../slices/stocksApiSlice";
import { useGetOpeningStocksQuery } from "../../slices/openingStocksApiSlice";
import { useParams } from "react-router-dom";

const StartStockScreen = () => {
  // State for filtering
  const [filterText, setFilterText] = useState("");

  // Data Fetches
  const { pageNumber } = useParams();
  const { data: productsData } = useGetProductsQuery({pageNumber});
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: salesData } = useGetSalesQuery();
  const { data: transfersData } = useGetTransfersQuery();
  const { data: stocksData } = useGetStocksQuery();
  const { data: openingStocksData } = useGetOpeningStocksQuery();

  // Extract Name & Price
  
  const getProductInfo = () => {
    return productsData?.products?.map((product) => ({
      name: product.name,
      price: product.price || 0,
    })) || [];
  };

  
  // Filter products based on search
  const filteredProducts = getProductInfo().filter((product) =>
    product.name.toLowerCase().includes(filterText.toLowerCase())
  );


  // Calculate Opening Quantity (Earliest Stock Date)
  const calculateOpeningQty = (productName) => {
    if (!stocksData?.stocks) return 0;
    
    const oldestDate = new Date(
      Math.min(...stocksData.stocks.map((stock) => new Date(stock.date)))
    );

    return stocksData.stocks
      .filter((stock) => new Date(stock.date).getTime() === oldestDate.getTime())
      .reduce((sum, stock) => {
        const matchedItems = stock.stockItems.filter((item) => item.name === productName);
        return sum + matchedItems.reduce((qtySum, item) => qtySum + item.qty, 0);
      }, 0);
  };

  // Calculate Supplied, Sold, and Transferred Quantities
  const calculateTotalSupplied = (productName) =>
    suppliesData?.supplies?.reduce((sum, supply) => {
      const found = supply.supplyItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  const calculateTotalTransferred = (productName) =>
    transfersData?.transfers?.reduce((sum, transfer) => {
      const found = transfer.transferItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  const calculateTotalSold = (productName) =>
    salesData?.sales?.reduce((sum, sale) => {
      const found = sale.saleItems.filter((item) => item.name === productName);
      return sum + found.reduce((qtySum, item) => qtySum + item.qty, 0);
    }, 0) || 0;

  // Calculate Balance Quantity
  const calculateBalanceQty = (productName) => {
    const openingQty = calculateOpeningQty(productName);
    const totalSupplied = calculateTotalSupplied(productName);
    const totalSold = calculateTotalSold(productName);
    const totalTransferred = calculateTotalTransferred(productName);
    
    return openingQty + totalSupplied - totalSold - totalTransferred;
  };

  // Export to Excel
  const handleExportExcel = () => {
    const exportData = getProductInfo().map((product) => ({
      Product: product.name,
      Price: product.price.toFixed(2),
      "Opening Qty": calculateOpeningQty(product.name),
      "Supplied Qty": calculateTotalSupplied(product.name),
      "Sold Qty": calculateTotalSold(product.name),
      "Transferred Qty": calculateTotalTransferred(product.name),
      "Balance Qty": calculateBalanceQty(product.name),
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stock Report");

    const wbout = XLSX.write(workbook, { type: "array", bookType: "xlsx" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "stock_report.xlsx");
  };

  const calculateAverageCost = (productName) => {
  const openingStockCost = openingStocksData?.stocks?.reduce((sum, stock) => {
    const matchedItems = stock.openingStockItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const suppliedCost = suppliesData?.supplies?.reduce((sum, supply) => {
    const matchedItems = supply.supplyItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const openingQty = calculateOpeningQty(productName);
  const totalSupplied = calculateTotalSupplied(productName);
  const totalQty = openingQty + totalSupplied;

  return totalQty > 0 ? (openingStockCost + suppliedCost) / totalQty : 0;
};

const calculateTotalCost = (productName) => {
  const openingStockCost = openingStocksData?.stocks?.reduce((sum, stock) => {
    const matchedItems = stock.openingStockItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  const suppliedCost = suppliesData?.supplies?.reduce((sum, supply) => {
    const matchedItems = supply.supplyItems.filter((item) => item.name === productName);
    return sum + matchedItems.reduce((costSum, item) => costSum + item.itemCost, 0);
  }, 0) || 0;

  return openingStockCost + suppliedCost;
};
const calculateBalanceCost = (productName) => {
  const avgUnitCost = calculateAverageCost(productName);
  const balanceQty = calculateBalanceQty(productName);
  
  return avgUnitCost * balanceQty;
};

const totalOpeningQty = filteredProducts.reduce((sum, product) => sum + calculateOpeningQty(product.name), 0);
const totalSuppliedQty = filteredProducts.reduce((sum, product) => sum + calculateTotalSupplied(product.name), 0);
const totalSoldQty = filteredProducts.reduce((sum, product) => sum + calculateTotalSold(product.name), 0);
const totalTransferredQty = filteredProducts.reduce((sum, product) => sum + calculateTotalTransferred(product.name), 0);
const totalBalanceQty = filteredProducts.reduce((sum, product) => sum + calculateBalanceQty(product.name), 0);
const totalCostSum = filteredProducts.reduce((sum, product) => sum + calculateTotalCost(product.name), 0);
const totalBalanceCost = filteredProducts.reduce((sum, product) => 
  sum + calculateBalanceCost(product.name), 0
);

  return (
    <>
      <Row className="align-items-center mb-4">
        <Col>
          <h1>Stock Overview</h1>
        </Col>
        <Row className="mb-3">
    <Col>
    <h4 className="fw-bold">Total Cost: ${totalCostSum.toFixed(2)} Total Bal Cost: ${totalBalanceCost.toFixed(2)}</h4>
    </Col>
   </Row>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Filter by product name"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </Col>
        <Col className="text-end">
          <Button variant="success" onClick={handleExportExcel}>
            Export to Excel
          </Button>
        </Col>
      </Row>

      <Table striped bordered hover responsive className="table-sm">
        <thead>
          <tr>
            <th>Product</th>
            <th>Price</th>
            <th>Op Qty</th>
            <th>Sup Qty</th>
            <th>Sold Qty</th>
            <th>Trans Qty</th>
            <th>Bal Qty</th>
            <th>Av Cost</th>
            <th>TotalCost</th>
            <th>Bal Cost</th>
          </tr>
        </thead>
        <tbody>
  {filteredProducts.map((product) => (
    <tr key={product.name}>
      <td>{product.name}</td>
      <td>${product.price.toFixed(2)}</td>
      <td>{calculateOpeningQty(product.name)}</td>
      <td>{calculateTotalSupplied(product.name)}</td>
      <td>{calculateTotalSold(product.name)}</td>
      <td>{calculateTotalTransferred(product.name)}</td>
      <td>{calculateBalanceQty(product.name)}</td>
      <td>${calculateAverageCost(product.name).toFixed(2)}</td>
      <td>${calculateTotalCost(product.name).toFixed(2)}</td>
      <td>${calculateBalanceCost(product.name).toFixed(2)}</td> {/* New Column */}
    </tr>
  ))}
  <tr className="fw-bold">
    <td>Total</td>
    <td>-</td>
    <td>{totalOpeningQty}</td>
    <td>{totalSuppliedQty}</td>
    <td>{totalSoldQty}</td>
    <td>{totalTransferredQty}</td>
    <td>{totalBalanceQty}</td>
    <td>-</td>
    <td>${totalCostSum.toFixed(2)}</td>
    <td>${totalBalanceCost.toFixed(2)}</td> {/* Total Balance Cost */}
  </tr>
</tbody>
      </Table>
    </>
  );
};

export default StartStockScreen;